/**
 * Email shape checking, for immediate feedback in the browser.
 *
 * ===========================================================================
 * THIS IS NOT THE AUTHORITY, AND IT MUST NEVER BECOME ONE.
 *
 * `backend/src/lib/email.js` validates every address that reaches `POST /api/orders`,
 * through `parseCreateOrderInput`, and rejects a malformed one with 400 whatever the
 * browser did. This file exists only so a customer is told at the field instead of after
 * a round trip — bypassing it changes nothing about what can be ordered.
 *
 * ===========================================================================
 * IT MIRRORS THE SERVER'S RULE, DELIBERATELY AND NARROWLY.
 *
 * Same shape, same lengths, same rejection of a doubled dot. A CLIENT RULE THAT IS
 * STRICTER THAN THE SERVER'S IS THE DANGEROUS DIRECTION: it refuses addresses the
 * product would happily accept, and the customer has no way to argue. So this does not
 * attempt RFC 5322, does not check for a "real" TLD, does not ban plus-addressing or
 * subdomains, and does not guess at deliverability — only sending can establish that.
 *
 * `type="email"` alone was not enough, which is the bug this closes: the browser's own
 * rule accepts `someone@localhost` (no dot in the domain), so a customer could pass the
 * field and only learn the address was wrong when the server refused the order.
 *
 * If the server's rule ever changes, change it there first and follow here.
 */

/** RFC 5321's limit on a forward path. Same constant as the server. */
const MAX_LENGTH = 254;
const MAX_LOCAL_LENGTH = 64;

/** One @, no whitespace, a dotted domain with no empty labels. Same shape as the server. */
const SHAPE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

/**
 * Why this address is unusable, in words a customer can act on — or null if it is fine.
 *
 * ONE MESSAGE FOR EVERY MALFORMATION, on purpose. "Enter a valid email address" is the
 * useful sentence; naming which of four rules was broken tells a person typing a typo
 * nothing they can use, and reads as pedantry.
 */
export function emailProblem(value: string): string | null {
  const normalized = value.trim().toLowerCase();

  if (normalized.length === 0) return 'Enter your email address.';
  if (normalized.length > MAX_LENGTH) return 'That email address is too long.';
  if (!SHAPE.test(normalized)) return 'Enter a valid email address, like you@example.com.';

  const [local] = normalized.split('@');
  if (local.length > MAX_LOCAL_LENGTH) return 'That email address is too long.';
  if (normalized.includes('..')) return 'Enter a valid email address, like you@example.com.';

  return null;
}

/** Non-throwing convenience, matching the server's `isValidEmail`. */
export function isValidEmail(value: string): boolean {
  return emailProblem(value) === null;
}
